<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customer.actions.creditCardAdd.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.customer.actions.creditCardAdd.add'), type: 'confirm', callback: confirm, disabled: !integration },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left ">
      <div
        v-if="loading"
        class="flex items-center justify-center gap-2 py-8 text-sm text-gray-700 dark:text-gray-200"
        role="status"
      >
        <o-loader size="20px" />
        <span>{{ $t('general.loading') }}</span>
      </div>

      <o-form v-else-if="integrations" ref="form">
        <o-field-group v-if="options.length > 1" class="block pb-3">
          <segment-field v-model="integration" :options="options" />
        </o-field-group>

        <div v-if="integration" class="px-3 rounded-md bg-white border dark:bg-gray-900">
          <component
            :is="components[integration.guid]"
            ref="integrationRef"
            :integration="_integration"
            :customer="customer"
          />
        </div>
      </o-form>

      <div v-else class="text-center text-gray-700 text-sm py-4">
        <p>{{ $t('components.customer.actions.creditCardAdd.noCompatible') }}</p>
      </div>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'
import Stripe from './addCreditCard/Stripe.vue'
import Square from './addCreditCard/Square.vue'

import {
  NumberField,
  SegmentField,
} from '@/components/fields'

import { Integration } from '~/_api/models/integration'

interface Props {
  portal?: string
}

interface Emits {
  (e: 'success'): void
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<Emits>()
const { $api, $bus, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const integrationRef = ref<any>()
const loading = ref(true)
const customer = ref<any>(null)
const integrations = ref<any>(null)
const components: Record<string, any> = {
  [Square.guid]: Square,
  [Stripe.guid]: Stripe,
}
const integration = ref<any>(null)
let latestIntegrationsRequest = 0

const _integration = computed(() => {
  return integration.value ? new Integration(integration.value) : null
})

const info = computed(() => {
  return $i18n.t('components.customer.actions.creditCardAdd.info', { name: customer.value?.displayName })
})

const options = computed(() => {
  const integrationsVal = integrations.value
  const componentKeys = Object.keys(components)

  if (integrationsVal) {
    const opts = Object.entries(integrationsVal)
      .filter(([key, value]: [string, any]) => {
        return value.enabled && componentKeys.includes(key)
      })
      .map(([key, value]: [string, any]) => {
        return {
          label: value.name,
          value,
        }
      })

    return opts
  }

  return []
})

watch(options, () => {
  const onlyOption = options.value.length === 1 ? options.value[0] : undefined

  if (onlyOption) {
    integration.value = onlyOption.value
  }
})

const open = (cust: any) => {
  customer.value = cust
  fetchIntegrations()
  dialog.value.open()
}

const fetchIntegrations = async () => {
  const requestId = ++latestIntegrationsRequest

  integrations.value = null
  integration.value = null
  loading.value = true

  try {
    const response = await $api.integration.active()

    if (requestId !== latestIntegrationsRequest) return

    integrations.value = response
  } catch (e: any) {
    if (requestId !== latestIntegrationsRequest) return

    console.error(e)
    $notification.error({ errors: e?.errors })
  } finally {
    if (requestId === latestIntegrationsRequest) {
      loading.value = false
    }
  }
}

const confirm = async () => {
  try {
    const params = await integrationRef.value.process()
    const response = await $api.integration.saveCardIntegration({
      guid: integration.value.guid,
      customerId: customer.value.idCustomer.toString(),
      params,
    })

    if (response.success) {
      emit('success')

      $bus.emit.updated('Customer', response.data)

      $notification.toast({
        title: $i18n.t('components.customer.actions.creditCardAdd.success'),
        variant: 'success',
      })

      return response.success
    }
  } catch (e: any) {
    console.log(e)
    Sentry.captureException(e)
    $notification.error({ errors: e.errors })
  }
}

const validate = () => {
  return form.value?.validate()
}

defineExpose({ open })
</script>
